import React from 'react';
import {
  $applyNodeReplacement,
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import type { ChartSpec } from './schema';
import { Text } from '@lifeomic/chroma-react/components/Text';

// Loaded on demand so Victory stays out of the DEFAULT_TRANSFORMERS graph that
// every MarkdownEditor and read-only viewer consumer imports.
const ChartRenderer = React.lazy(() =>
  import('./ChartRenderer').then(module => ({ default: module.ChartRenderer })),
);

export type SerializedChartNode = Spread<
  { spec: ChartSpec | null },
  SerializedLexicalNode
>;

/**
 * Block decorator for a <!--chart ... --> comment. Stores a validated spec
 * (or null if the payload was invalid) and renders {@link ChartRenderer}.
 */
export class ChartNode extends DecoratorNode<React.ReactNode> {
  __spec: ChartSpec | null;

  static getType() {
    return 'dynamic-chart';
  }

  static clone(node: ChartNode) {
    return new ChartNode(node.__spec, node.__key);
  }

  constructor(spec: ChartSpec | null, key?: NodeKey) {
    super(key);
    this.__spec = spec;
  }

  getSpec(): ChartSpec | null {
    return this.getLatest().__spec;
  }

  createDOM() {
    const div = document.createElement('div');
    div.setAttribute('data-lexical-dynamic-chart', 'true');
    return div;
  }

  updateDOM() {
    return false;
  }

  decorate() {
    const spec = this.__spec;
    if (!spec) {
      return (
        <Text data-testid="dynamic-chart-error">
          Unable to display this chart.
        </Text>
      );
    }
    return (
      <React.Suspense fallback={null}>
        <ChartRenderer spec={spec} />
      </React.Suspense>
    );
  }

  isInline(): boolean {
    return false;
  }

  static importJSON(serializedNode: SerializedChartNode): ChartNode {
    return $applyNodeReplacement(
      new ChartNode(serializedNode.spec),
    ) as ChartNode;
  }

  exportJSON(): SerializedChartNode {
    return {
      ...super.exportJSON(),
      spec: this.__spec,
      type: 'dynamic-chart',
      version: 1,
    };
  }
}

export const $createChartNode = (spec: ChartSpec | null) => {
  return $applyNodeReplacement(new ChartNode(spec));
};

export const $isChartNode = (
  node: LexicalNode | null | undefined,
): node is ChartNode => {
  return node instanceof ChartNode;
};
